import React, { useState } from "react";
import { X, Copy, Check, RotateCw } from "lucide-react";
import { toast } from "react-hot-toast";

const WebhookDetails = ({ isOpen, onClose, data, onRetrigger, isRetriggering }) => {
  const [copiedUUID, setCopiedUUID] = useState(false);
  const [activeTab, setActiveTab] = useState("payload"); // "payload" or "headers"

  if (!isOpen || !data) return null;

  const detailItem = (label, value, copyable = false) => {
    const handleCopy = () => {
      navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    };

    return (
      <div className="flex flex-col mb-4">
        <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-gray-900 break-all">{value || "N/A"}</span>
          {copyable && value && (
            <button
              onClick={handleCopy}
              className="text-gray-400 transition-colors hover:text-gray-600 animate-fade-in"
              title={`Copy ${label}`}
            >
              <Copy size={14} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleCopyUUID = () => {
    navigator.clipboard.writeText(data.uuid);
    setCopiedUUID(true);
    toast.success("Webhook UUID copied to clipboard");
    setTimeout(() => setCopiedUUID(false), 2000);
  };

  const formatAmount = (amount, currency) => {
    if (amount === undefined || amount === null) return null;
    const num = Number(amount);
    const formatted = isNaN(num) ? amount : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
    return currency ? `${currency} ${formatted}` : formatted;
  };

  const statusStyles = {
    success: "bg-green-100 text-green-800 border-green-200",
    failed: "bg-red-100 text-red-800 border-red-200",
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  };

  const currentStatus = data.status?.toLowerCase() || "unknown";

  // Extract payload data
  const payloadData = data.payload?.data;
  const fees = payloadData?.fees || {};
  const customer = payloadData?.customer;

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden h-[90vh] mx-4 w-full max-w-4xl bg-white rounded-2xl shadow-2xl transition-all transform flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <div className="flex gap-3 items-center">
            <h3 className="text-lg font-semibold text-gray-900">Provider Webhook Details</h3>
            <span className={`px-3 py-0.5 rounded-full text-xs font-semibold border ${statusStyles[currentStatus] || "bg-gray-150 text-gray-800 border-gray-300"}`}>
              {data.status || "UNKNOWN"}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-8">
          {/* Webhook Meta Information */}
          <div className="mb-6">
            <h4 className="pb-2 mb-4 text-xs font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
              Webhook Information
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {detailItem("Webhook ID", data.id?.toString())}
              <div className="flex flex-col mb-4">
                <span className="text-xs tracking-wider text-gray-500 uppercase">Webhook UUID</span>
                <div className="flex gap-2 items-center">
                  <span className="font-mono text-sm font-medium text-gray-900 break-all">{data.uuid}</span>
                  <button
                    onClick={handleCopyUUID}
                    className="text-gray-400 transition-colors hover:text-gray-600"
                    title="Copy UUID"
                  >
                    {copiedUUID ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              {detailItem("Provider", data.provider?.toUpperCase())}
              {detailItem("Retries", data.retriesCount?.toString() || "0")}
              {detailItem("Created At", data.createdAt ? new Date(data.createdAt).toLocaleString() : "N/A")}
              {detailItem("Updated At", data.updatedAt ? new Date(data.updatedAt).toLocaleString() : "N/A")}
            </div>

            {data.errorMessage && (
              <div className="p-4 mt-4 bg-red-50 rounded-xl border border-red-200">
                <span className="block mb-1 text-xs font-bold text-red-800 uppercase">Error Message</span>
                <p className="text-sm text-red-700 break-all">{data.errorMessage}</p>
              </div>
            )}
          </div>

          {/* Conditional Payload Metadata Sections */}
          {payloadData && (
            <>
              {/* Transaction / Event Information */}
              <div className="mb-6">
                <h4 className="pb-2 mb-4 text-xs font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                  Transaction Information
                </h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {payloadData.uuid && detailItem("Transaction UUID", payloadData.uuid, true)}
                  {payloadData.reference && detailItem("Reference", payloadData.reference, true)}
                  {payloadData.description && detailItem("Description", payloadData.description)}
                  {payloadData.paymentGateway && detailItem("Payment Gateway", payloadData.paymentGateway)}
                  {payloadData.paymentChannel && detailItem("Payment Channel", payloadData.paymentChannel)}
                  {payloadData.paymentGatewayReference && detailItem("Gateway Reference", payloadData.paymentGatewayReference, true)}
                  {payloadData.cardholderName && detailItem("Cardholder Name", payloadData.cardholderName)}
                  {payloadData.status && detailItem("Payload Status", payloadData.status)}
                  {payloadData.merchantId && detailItem("Merchant ID", payloadData.merchantId.toString())}
                </div>
              </div>

              {/* Amounts and Fees */}
              {(payloadData.sourceAmount || payloadData.destinationAmount || fees.sourceTotalFee || payloadData.sourceTotalFee) && (
                <div className="mb-6">
                  <h4 className="pb-2 mb-4 text-xs font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                    Amounts & Fees
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {detailItem("Source Amount", formatAmount(payloadData.sourceAmount, payloadData.sourceCurrency))}
                    {detailItem("Destination Amount", formatAmount(payloadData.destinationAmount, payloadData.destinationCurrency))}
                    {detailItem("Total Source Amount", formatAmount(payloadData.sourceTotalAmount, payloadData.sourceCurrency))}
                    {detailItem("Total Destination Amount", formatAmount(payloadData.destinationTotalAmount, payloadData.destinationCurrency))}
                    {payloadData.exchangeRate && detailItem("Exchange Rate", payloadData.exchangeRate.toString())}

                    {/* Source Fees */}
                    {detailItem("Source Total Fee", formatAmount(fees.sourceTotalFee ?? payloadData.sourceTotalFee, payloadData.sourceCurrency))}
                    {detailItem("Source Gateway Fee", formatAmount(fees.sourceGatewayFee ?? payloadData.sourceGatewayFee, payloadData.sourceCurrency))}
                    {detailItem("Source Platform Fee", formatAmount(fees.sourcePlatformFee ?? payloadData.sourcePlatformFee, payloadData.sourceCurrency))}

                    {/* Destination Fees */}
                    {detailItem("Destination Total Fee", formatAmount(fees.destinationTotalFee ?? payloadData.destinationTotalFee, payloadData.destinationCurrency))}
                    {detailItem("Destination Gateway Fee", formatAmount(fees.destinationGatewayFee ?? payloadData.destinationGatewayFee, payloadData.destinationCurrency))}
                    {detailItem("Destination Platform Fee", formatAmount(fees.destinationPlatformFee ?? payloadData.destinationPlatformFee, payloadData.destinationCurrency))}
                  </div>
                </div>
              )}

              {/* Customer Details */}
              {customer && (
                <div className="mb-6">
                  <h4 className="pb-2 mb-4 text-xs font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
                    Customer Details
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {detailItem("Name", `${customer.firstname || ""} ${customer.lastname || ""}`.trim() || null)}
                    {detailItem("Email", customer.email, true)}
                    {customer.uuid && detailItem("Customer UUID", customer.uuid, true)}
                    {customer.id && detailItem("Customer ID", customer.id.toString())}
                    {customer.cashOnRailsCode && detailItem("CashOnRails Code", customer.cashOnRailsCode)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Code Tabs */}
          <div className="flex flex-col flex-1 mt-4">
            <div className="flex mb-4 border-b border-gray-200">
              <button
                className={`py-2 px-4 text-sm font-semibold border-b-2 transition-all ${activeTab === "payload"
                  ? "border-emerald-600 text-emerald-800"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setActiveTab("payload")}
              >
                Payload Data
              </button>
              <button
                className={`py-2 px-4 text-sm font-semibold border-b-2 transition-all ${activeTab === "headers"
                  ? "border-emerald-600 text-emerald-800"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setActiveTab("headers")}
              >
                Request Headers
              </button>
            </div>

            <div className="flex-1 bg-gray-900 text-green-400 p-5 rounded-2xl overflow-y-auto max-h-[350px]">
              {activeTab === "payload" ? (
                <div>
                  {data.payload?.event && (
                    <div className="pb-2 mb-3 border-b border-gray-800">
                      <span className="block font-sans text-xs tracking-widest text-gray-500 uppercase">Event</span>
                      <span className="text-sm font-semibold text-white">{data.payload.event}</span>
                    </div>
                  )}
                  <pre className="text-sm whitespace-pre-wrap break-all">
                    {JSON.stringify(data.payload, null, 2)}
                  </pre>
                </div>
              ) : (
                <pre className="text-sm whitespace-pre-wrap break-all">
                  {JSON.stringify(data.headers, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 justify-center items-center px-6 py-4 bg-gray-50 border-t md:justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 font-bold text-gray-700 bg-white rounded-xl border border-gray-300 shadow-sm transition-all text-sm hover:bg-gray-50 active:scale-95"
          >
            Close
          </button>

          <button
            onClick={() => onRetrigger(data)}
            disabled={isRetriggering}
            className="flex gap-2 items-center px-5 py-2.5 font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-sm transition-all text-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRetriggering ? (
              <>
                <RotateCw className="w-4 h-4 animate-spin" />
                Retriggering...
              </>
            ) : (
              <>
                <RotateCw className="w-4 h-4" />
                Retrigger Webhook
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebhookDetails;
